import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  Grid,
  Button,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import theme from "../../theme";
import useButtonStyles from "../../styles/useButtonStyles";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import { useParams } from "react-router-dom";
import formatDate, {
  dateFormat,
  getFormattedDate,
} from "../../util/dateFormat";
import BasicTextField from "../forms/formElements/BasicTextField";
import SplitLayout from "../forms/formLayouts/SplitLayout";
import BasicModal from "../ui/BasicModal";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useAppSelector } from "../../redux/hooks";
import { DateConstants } from "../../constants/AppConstants";
interface AddVoucherToReportProps {
  voucherNumbers: string[];
  onSearch: (searchValue: string) => void;
  onAdd: (selectedVoucherNumber: string) => void;
}
const AddVoucherToReport: React.FC<AddVoucherToReportProps> = ({
  voucherNumbers,
  onSearch,
  onAdd,
}) => {
  const buttonStyles = useButtonStyles();
  const [dateError, setDateError] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [addError, setAddError] = useState(false);
  const [clearForm, setClearForm] = useState(false);
  const [showXMark, setShowXMark] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [addSuccess, setAddSuccess] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [voucherData, setVoucherData] = useState<any>(null);
  const [addErrorMessage, setAddErrorMessage] = useState("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [isDateAcceptedEmpty, setIsDateAcceptedEmpty] = useState(true);
  const [axiosErrorMessage, setAxiosErrorMessage] = useState<string>("");

  const params = useParams();
  const { PVoucherId } = params;
  const physicalVoucherList = useAppSelector(
    (state: any) => state.persistReducers.financeSlice?.physicalVoucher
  );
  const filterHeader = physicalVoucherList.filter(
    (item: any) => item.reportIdDetail === PVoucherId
  );
  const headerDetails = filterHeader[0];

  const isIssueDateValid = () => {
    if (issueDate && voucherData.expiry_date && voucherData.issue_date) {
      const issueDateEpoch = new Date(issueDate).getTime();
      const expiryDateEpoch = new Date(voucherData.expiry_date).getTime();
      const voucherIssueDateEpoch = new Date(voucherData.issue_date).getTime();

      return (
        issueDateEpoch >= voucherIssueDateEpoch &&
        issueDateEpoch <= expiryDateEpoch
      );
    }
    return false;
  };

  const isAcceptedDateValid = () => {
    const currentDate = new Date().toISOString().split("T")[0];
    if (issueDate && voucherData.expiry_date && voucherData.issue_date) {
      return issueDate <= currentDate;
    }
    return false;
  };

  const errorHelperText = () => {
    if (!isDateAcceptedEmpty && issueDate && !isIssueDateValid()) {
      return (
        <Typography
          sx={{
            fontFamily: theme.font?.fontFamily.primary,
            fontSize: theme.font?.fontSize.tableBody,
            fontWeight: theme.font?.fontWeight.slightThick,
            color: theme.color?.error,
            position: "absolute",
          }}
        >
          *Accepted date should be between Issue date and Expiry date
        </Typography>
      );
    } else if (issueDate && !isAcceptedDateValid()) {
      return (
        <Typography
          sx={{
            fontFamily: theme.font?.fontFamily.primary,
            fontSize: theme.font?.fontSize.tableBody,
            fontWeight: theme.font?.fontWeight.slightThick,
            color: theme.color?.error,
            position: "absolute",
          }}
        >
          *Accepted date cannot be in the future
        </Typography>
      );
    } else {
      return "";
    }
  };

  const handleSearch = async () => {
    onSearch(searchValue);

    try {
      const response = await axios.post(
        `${
          process.env.REACT_APP_API_URL + APIConstants.getAllPhysicalVoucher
        }?search=${searchValue}&searchAbsolute=true`
      );
      if (response?.data?.data?.result[0]?._source) {
        const result = response?.data?.data?.result;
        const mapped = result?.map((voucher: any) => {
          const voucherDetails = voucher._source;
          return {
            voucherData: {
              amount: voucherDetails.amount ? voucherDetails.amount : "-",
              expiry_date: voucherDetails.expiry_date
                ? formatDate(voucherDetails.expiry_date)
                : "-",
            },
          };
        });

        setVoucherData(response.data.data.result[0]._source);
        setUploadError(false);
        setShowCheckmark(true);
      } else {
        setUploadError(true);
        setShowXMark(true);
      }
    } catch (error: any) {
      setAxiosErrorMessage(
        error?.response?.data?.error ?? "Something went wrong"
      );
      setUploadError(true);
      setShowXMark(true);
    }
  };

  const handleAdd = async () => {
    setAddLoading(true);
    const reportId = window.location.pathname.split("/").pop();
    try {
      const data = {
        voucherNumber: searchValue,
        acceptedDate: getFormattedDate(issueDate, dateFormat.DDWithSlash),
        reportId,
        merchantId: headerDetails.merchantID,
      };
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.addVoucherIntoReport,
        data
      );
      setAddSuccess(true);
      setAddError(false);
      setAddErrorMessage("");
      setTimeout(() => {
        window.location.reload();
      }, 4000);
    } catch (error: any) {
      console.error("error", error);
      setAddSuccess(false);
      setAddError(true);
      setAddErrorMessage(
        error?.response?.data?.Message ?? "Something went wrong"
      );
    } finally {
      setAddLoading(false);
      setLoading(false);
      setShowModal(true);
      setClearForm(true);
      setTimeout(() => {
        setClearForm(false);
      }, 2000);
    }
  };

  useEffect(() => {
    setVoucherData(null);
  }, [searchValue]);

  useEffect(() => {
    if (showCheckmark || showXMark) {
      const timer = setTimeout(() => {
        setShowCheckmark(false);
        setShowXMark(false);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [showCheckmark, showXMark]);

  return (
    <Grid container alignItems="center">
      <Grid item container sx={{ ml: 5 }} alignItems="center" height={"100%"}>
        <Grid item>
          <Typography
            fontFamily={theme?.font?.fontFamily.primary}
            sx={{ color: theme.color?.primary }}
          >
            Search Voucher No.
          </Typography>
          <TextField
            sx={{ mr: 3 }}
            variant="outlined"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={handleSearch}
                    variant="contained"
                    sx={buttonStyles.containedSecondary}
                    disabled={!searchValue.trim()}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="primary" />
                    ) : (
                      <>
                        {showCheckmark && (
                          <CheckCircleOutlineIcon style={{ color: "white" }} />
                        )}
                        {showXMark && (
                          <BasicModal
                            title={"Voucher not found"}
                            setState={setShowModal}
                          />
                        )}
                        {!showCheckmark && !showXMark && "Search"}
                      </>
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {voucherData && (
        <Grid item container sx={{ ml: 5, mt: 2 }}>
          <Typography
            fontFamily={theme?.font?.fontFamily.primary}
            sx={{ color: theme.color?.primary }}
          >
            Voucher Data:
          </Typography>
          <Box mb={2} width="100%">
            <SplitLayout columns={[3, 3, 3, 3]}>
              <BasicTextField
                label={"Amount"}
                defaultValue={voucherData.amount}
                setState={setVoucherData}
                stateKey="id"
                clear={clearForm}
                readonly={true}
                placeholder={""}
              />
              <FormControl variant="outlined">
                <Typography
                  fontFamily={theme?.font?.fontFamily.primary}
                  sx={{ color: theme.color?.primary }}
                >
                  Date Accepted
                </Typography>

                <TextField
                  id="from-date"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  inputProps={{
                    min: voucherData.issue_date
                      ? formatDate(voucherData.issue_date)
                      : undefined,
                    max: voucherData.expiry_date
                      ? formatDate(voucherData.expiry_date)
                      : undefined,
                  }}
                  onSelect={() => {
                    setIsDateAcceptedEmpty(false);
                  }}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: {
                      max: DateConstants.maxDate,
                    },
                  }}
                  helperText={errorHelperText()}
                />
              </FormControl>
              <BasicTextField
                label="Expiry Date"
                setState={setVoucherData}
                placeholder=""
                defaultValue={formatDate(voucherData.expiry_date)}
                stateKey="voucherNo"
                variant="outlined"
                align="left"
                required={true}
                disableSubmit={() => {}}
                clear={clearForm}
                readonly={true}
              />
              <Button
                onClick={handleAdd}
                variant="contained"
                disabled={isDateAcceptedEmpty || !isIssueDateValid()}
                sx={{
                  ...buttonStyles.containedPrimary,
                  mt: 3,
                  ml: 3,
                }}
              >
                {addLoading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  "Add Voucher"
                )}
              </Button>
            </SplitLayout>
          </Box>
        </Grid>
      )}
      {showModal && (
        <BasicModal
          title={addSuccess ? "Voucher added Successfully!" : addErrorMessage}
          success={addSuccess}
          setState={setShowModal}
        />
      )}
    </Grid>
  );
};

export default AddVoucherToReport;
